<template>
  <form class="w-full flex flex-col gap-y-3" @submit.prevent="onSubmit">
    <h1 class="text-xl mb-3">
      {{ createUser ? "Create new user" : "Login to timeplat.eu" }}
    </h1>
    <div v-if="createUser">
      <FormLabel
        :value="email"
        :is-valid="(v) => isValidEmail(v as string)"
        required
        >Email</FormLabel
      >
      <UInput
        v-if="createUser"
        v-model="email"
        size="xl"
        name="email"
        placeholder="Email"
        class="w-full"
      />
    </div>
    <div>
      <FormLabel :value="username" required>Username</FormLabel>
      <UInput
        v-model="username"
        :disabled="!!fixedUsername"
        :trailing-icon="fixedUsername ? 'i-mdi-lock-outline' : ''"
        size="xl"
        name="username"
        placeholder="Username"
        class="w-full"
      />
    </div>
    <div>
      <FormLabel :value="password" required>Password</FormLabel>
      <UInput
        v-model="password"
        size="xl"
        type="password"
        name="password"
        placeholder="Password"
        class="w-full"
      />
    </div>
    <div class="mt-6">
      <div class="flex items-center gap-x-3">
        <UButton
          ref="loginBtn"
          :disabled="loading"
          :loading="loading"
          type="submit"
          class="cursor-pointer"
          size="xl"
          color="success"
          variant="solid"
        >
          {{ createUser ? "Create account" : "Log in" }}
        </UButton>
        <div>
          <UButton
            class="cursor-pointer underline"
            variant="link"
            color="neutral"
            @click="
              () => {
                createUser = !createUser;
              }
            "
            >{{
              createUser ? "I want to login" : "I want to create a user"
            }}</UButton
          >
        </div>
      </div>
      <pre
        v-if="error"
        class="text-error-500 font-mono whitespace-break-spaces text-sm mt-6 mb-3"
        >{{ error }}
      </pre>
      <UAlert
        v-if="successMsg"
        :description="successMsg"
        variant="subtle"
        color="success"
        icon="i-mdi-check"
        class="mb-3 mt-6 light:text-success-600"
      >
        {{ successMsg }}
      </UAlert>
    </div>
  </form>
</template>

<script setup lang="ts">
import { isValidEmail } from "~~/shared/utils/validate";

const props = defineProps({
  forceLogin: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  forceCreate: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  fixedUsername: {
    type: String,
    required: false,
    default: () => undefined,
  },
});

const username = ref(props.fixedUsername || "");
const password = ref("");
const email = ref("");
const error = ref("");
const successMsg = ref("");
const loading = ref(false);
const createUser = ref(props.forceCreate);

const emit = defineEmits<{
  "login-success": [];
  "create-user-success": [];
}>();

function resetInputs() {
  username.value = props.fixedUsername || "";
  password.value = "";
  email.value = "";
}

async function onSubmit(event: Event) {
  event.preventDefault();
  error.value = "";
  if (loading.value)
    throw new Error(
      "Detected login form submit while already waiting on response",
    );
  loading.value = true;

  try {
    const url = "/api" + (createUser.value ? "/user" : "/auth/login");
    await $fetch(url, {
      method: "POST",
      body: JSON.stringify({
        username: username.value,
        password: password.value,
        ...(createUser.value ? { email: email.value } : {}),
      }),
      headers: {
        "Content-Type": "application/json",
      },
      onResponseError: async (res) => {
        console.error(res);
        error.value = await parseApiError(res);
      },
      onResponse: async (res) => {
        if (createUser.value && [200, 204].includes(res.response.status)) {
          successMsg.value =
            "Success! We've sent you an email to finish account setup";
          resetInputs();
          emit("create-user-success");
        } else if (res.response.status === 200) {
          console.log("login success ???");
          emit("login-success");
        }
      },
    });
  } catch (e: unknown) {
    console.error(e);
    error.value = await parseApiError(e as Error);
  } finally {
    loading.value = false;
  }

  return false;
}
</script>
